import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import type { BLUEPRINT_NAMES } from '../mappings/blueprintNameMapping.ts';
import type { WHITEBOARD_TEMPLATE_IDS } from '../mappings/whiteboardMappings.ts';

const deserializeTemplateUsage = (
	serializedTemplateUsage: string | undefined,
	templateList: Array<WHITEBOARD_TEMPLATE_IDS | BLUEPRINT_NAMES>,
): Partial<Record<WHITEBOARD_TEMPLATE_IDS | BLUEPRINT_NAMES, number>> => {
	try {
		const parsedTemplateUsage: Partial<Record<WHITEBOARD_TEMPLATE_IDS | BLUEPRINT_NAMES, number>> =
			JSON.parse(serializedTemplateUsage || '{}');

		if (typeof parsedTemplateUsage !== 'object') {
			return {};
		}

		// There's no control over the shape of the object that's deserialized,
		// so we need to filter out any keys and values that aren't valid.
		const templateListSet = new Set(templateList);
		return Object.keys(parsedTemplateUsage).reduce(
			(acc, key) =>
				templateListSet.has(key as WHITEBOARD_TEMPLATE_IDS | BLUEPRINT_NAMES)
					? { ...acc, [key]: parsedTemplateUsage[key as WHITEBOARD_TEMPLATE_IDS | BLUEPRINT_NAMES] }
					: acc,
			{},
		);
	} catch (e) {
		return {};
	}
};

const serializeTemplateUsage = (
	deserializedTemplateUsage: Partial<Record<WHITEBOARD_TEMPLATE_IDS | BLUEPRINT_NAMES, number>>,
): string => {
	return JSON.stringify(deserializedTemplateUsage);
};

export const usePTV2TemplateUsage = (
	serializedTemplateUsage: string | undefined,
	templateList: Array<WHITEBOARD_TEMPLATE_IDS | BLUEPRINT_NAMES>,
	templateUsageKey: string = PERSONALIZED_TEMPLATES_V2_ONBOARDING_STATE_KEYS.templateUsageValues,
): {
	deserializedTemplateUsage: Partial<Record<BLUEPRINT_NAMES | WHITEBOARD_TEMPLATE_IDS, number>>;
	incrementTemplateUsage: (templateId: WHITEBOARD_TEMPLATE_IDS | BLUEPRINT_NAMES) => void;
} => {
	const { setOnboardingState } = useSetOnboardingState();
	const deserializedTemplateUsage = deserializeTemplateUsage(serializedTemplateUsage, templateList);

	const incrementTemplateUsage = (templateId: WHITEBOARD_TEMPLATE_IDS | BLUEPRINT_NAMES): void => {
		const updatedTemplateUsage = {
			...deserializedTemplateUsage,
			[templateId]: (deserializedTemplateUsage[templateId] || 0) + 1,
		};

		setOnboardingState({
			key: templateUsageKey,
			value: serializeTemplateUsage(updatedTemplateUsage),
		});
	};

	return {
		deserializedTemplateUsage,
		incrementTemplateUsage,
	};
};
